import { Controller } from "@hotwired/stimulus"
import TintOverlayView from "../google_maps/tint_overlay_view"
import { drawPolygon } from "../google_maps/polygon"

export default class extends Controller {

  static targets = [
    "map",
    "closeButtonTemplate",
    "infoWindowHeaderTemplate",
    "infoWindowContentTemplate"
  ]
  static values = {
    config: Object,
    spots: Array,
    selectedSpotId: Number,
    spotIcon: String,
    spotSelectedIcon: String,
  }

  disconnect() {
    if(this.markers) {
      this.markers.forEach(({marker}) => marker.map = null)
      this.markers = null
    }
    if(this.map) {
      delete(this.map)
      this.map = null
    }
  }

  async renderMap() {
    if (this.map) {
      this.#refreshMarkers()
      return
    }
    this.map = await this.#initMap()
    this.maxZoomService = new google.maps.MaxZoomService()
    this.infowindow = new google.maps.InfoWindow({maxWidth: 250});
    this.markers = this.spotsValue.map((spot) => this.#createSpotMarker(spot))
    this.configValue.piers.forEach((points) => drawPolygon({ map: this.map, points }))
    this.#setInitialZoomLevel()
  }

  async #initMap() {
    console.debug("[SpotSelectionMap] initializing map")

    const { Map } = await google.maps.importLibrary("maps");

    const { center, zoom, mapType, mapId } = this.configValue
    const map = new Map(this.mapTarget, {
      center,
      zoom,
      mapTypeId: mapType,
      mapTypeControl: false,
      rotateControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      mapId
    })
    map.setTilt(0)

    const tintOverlay = new TintOverlayView();
    tintOverlay.setMap(map);

    const closeButton = document.importNode(this.closeButtonTemplateTarget.content, true).firstElementChild
    closeButton.addEventListener("click", (event) => {
      event.preventDefault()
      this.dispatch("close")
    })
    map.controls[google.maps.ControlPosition.TOP_RIGHT].push(closeButton)

    return map
  }

  confirmSpotSelection() {
    console.debug("[SpotSelectionMap] confirmed", this.selectedSpotIdValue)

    this.dispatch("spotSelected", { detail: { spotId: this.selectedSpotIdValue } })
  }

  #createSpotMarker(spot) {
    const { position, number, id } = spot

    const markerImage = document.createElement('img');
    markerImage.width = 36
    markerImage.height = 60

    const marker = new google.maps.marker.AdvancedMarkerElement({
      map: this.map,
      position: position,
      title: number,
      content: markerImage,
      gmpClickable: true
    })
    marker.addListener('gmp-click', () => this.#updateSelectedSpot({ markerImage, spot, marker }));

    if(id === this.selectedSpotIdValue) {
      this.#updateSelectedSpot({ markerImage, spot, marker })
    } else {
      this.#showMarkerAsDeselected(markerImage)
    }

    return { markerImage, spot, marker }
  }

  #setInitialZoomLevel() {
    if(this.selectedSpotIdValue) { return }
    if(this.markers.length < 1) { return }

    const bounds = new google.maps.LatLngBounds();
    this.markers.forEach(({ marker }) => {
      bounds.extend(marker.position)
    })
    this.#setMaxZoomAfterNextBoundUpdate(this.configValue.maxInitialZoom)
    this.map.fitBounds(bounds)
  }

  #setMaxZoomAfterNextBoundUpdate(maxZoom) {
    if(this.boundsChangedHandler) { this.boundsChangedHandler.remove() }
    this.boundsChangedHandler = this.map.addListener("bounds_changed", () => {
      this.boundsChangedHandler.remove()
      this.boundsChangedHandler = null
      if(this.map.zoom > maxZoom) {
        this.map.setZoom(maxZoom)
      }
    })
  }

  #refreshMarkers() {
    this.markers.forEach(({ markerImage, spot, marker }) => {
      if(spot.id === this.selectedSpotIdValue) {
        this.#updateSelectedSpot({ markerImage, spot, marker })
      }
    })
  }

  async #updateSelectedSpot({markerImage, spot, marker}) {
    this.#updateSelectedMarker(markerImage)

    const infoWindowHeader = document.importNode(this.infoWindowHeaderTemplateTarget.content, true).firstElementChild
    const infoWindowContent = document.importNode(this.infoWindowContentTemplateTarget.content, true).firstElementChild
    const spotNumberSpan = infoWindowHeader.getElementsByClassName("spot-number")[0]
    const spotDescriptionSpan = infoWindowContent
    const selectButton = infoWindowHeader.getElementsByClassName("select-button")[0]

    spotNumberSpan.textContent = spot.number
    spotDescriptionSpan.textContent = spot.description

    selectButton.addEventListener("click", (event) => {
      event.preventDefault()
      this.confirmSpotSelection()
    })

    this.infowindow.setHeaderContent(infoWindowHeader)
    this.infowindow.setContent(infoWindowContent)

    this.map.panTo(marker.position)
    this.infowindow.open({
      anchor: marker,
      map: this.map
    });

    this.selectedSpotIdValue = spot.id

    const { zoom } = await this.maxZoomService.getMaxZoomAtLatLng(marker.position)
    const targetZoomLevel = Math.min(zoom, this.configValue.maxSpotZoom)
    if(targetZoomLevel > this.map.zoom) {
      this.map.setZoom(targetZoomLevel)
    }
  }

  #updateSelectedMarker(imgElement) {
    if (this.selectedMarkerImage) {
      this.#showMarkerAsDeselected(this.selectedMarkerImage)
    }
    this.#showMarkerAsSelected(imgElement)
  }

  #showMarkerAsSelected(imgElement) {
    this.selectedMarkerImage = imgElement
    imgElement.src = this.spotSelectedIconValue
  }

  #showMarkerAsDeselected(imgElement) {
    imgElement.src = this.spotIconValue
  }
}
